import * as tus from 'tus-js-client';
import { create } from 'zustand';
import { MediaObject } from 'src/api/services/MediaClient';
import { HookFormMediaObject } from 'src/components/Form/S3UploadInput';
import {
  VideoTemplateExampleResponse,
  VideoTemplateResponse,
} from 'src/lib/services/api/video-templates-api';

export type Options = {
  isMultiStep?: boolean;
  currentStep?: number;
  selectedTemplate?: VideoTemplateResponse & {
    exampleTemplate?: VideoTemplateExampleResponse['footage'];
  };
  // TODO: temporary type, remove after request form refactor
  isRepurpose?: boolean;
  initialValues?: Record<string, any>;
  mediaLinks?: MediaLink[];
};

// Define the media link type
export interface MediaLink {
  url: string;
}

// Define the selected media item type for storing with URLs
export interface SelectedMediaItem {
  id: string;
  url: string;
  directoryPath?: string | null;
}

export interface UploadingFileWithProgress {
  id: string;
  file: File;
  progress: number;
  preview?: string;
  uploadInstance?: tus.Upload;
  status: 'uploading' | 'completed' | 'error';
  error?: string;
}

interface RequestFormStore {
  modalState: number;
  isModalOpen: boolean;
  isMinimized?: boolean;
  requestName?: string;
  uploadingFiles: MediaObject[] | HookFormMediaObject[];
  selectedMediaIds: string[];
  selectedMediaItems: SelectedMediaItem[];
  mediaLinks: MediaLink[];
  selectedTemplate?: VideoTemplateResponse & {
    exampleTemplate?: VideoTemplateExampleResponse['footage'];
  };
  uploadingFilesWithProgress: UploadingFileWithProgress[];
  addUploadingFile: (file: UploadingFileWithProgress) => void;
  updateUploadProgress: (fileId: string, progress: number) => void;
  updateUploadStatus: (
    fileId: string,
    status: 'uploading' | 'completed' | 'error',
    error?: string,
  ) => void;
  removeUploadingFile: (fileId: string) => void;
  mediaGalleryRefreshTrigger: number;
  currentRequestId?: string;
  isMultiStep?: boolean;
  currentStep?: number;
  flowStep?: 'style' | 'media';
  selectedStyle?: 'template' | 'custom';
  // TODO: temp prop until request refactor
  isRepurpose?: boolean;
  initialValues?: Record<string, any>;

  setRequestName: (requestName?: string) => void;
  setUploadingFiles: (files?: MediaObject[] | HookFormMediaObject[]) => void;
  setSelectedMediaIds: (selectedMediaIds: string[]) => void;
  setSelectedMediaItems: (selectedMediaItems: SelectedMediaItem[]) => void;
  addSelectedMediaItem: (mediaItem: SelectedMediaItem) => void;
  removeSelectedMediaItem: (mediaId: string) => void;
  setMediaLinks: (links: MediaLink[]) => void;
  addMediaLink: (link: MediaLink) => void;
  removeMediaLink: (index: number) => void;
  setCurrentRequestId: (id?: string) => void;
  setCurrentStep: (step: number) => void;
  setFlowStep: (flowStep: 'style' | 'media') => void;
  setSelectedStyle: (selectedStyle: 'template' | 'custom') => void;
  setSelectedTemplate: (
    template:
      | (VideoTemplateResponse & { exampleTemplate?: VideoTemplateExampleResponse['footage'] })
      | undefined,
  ) => void;

  triggerMediaGalleryRefresh: () => void;
  openModal: (options?: Options) => void;
  minimizeModal: (options?: Options) => void;
  discardModal: () => void;
  reset: () => void;
}

const defaultValues = {
  modalState: 0,
  isModalOpen: false,
  isMinimized: undefined,
  flowStep: 'style' as 'style' | 'media',
  selectedStyle: 'template' as 'template' | 'custom',
  requestName: undefined,
  uploadingFiles: [],
  uploadingFilesWithProgress: [],
  selectedMediaIds: [],
  selectedMediaItems: [], // Store media items with URLs
  mediaLinks: [], // Initialize with empty array
  mediaGalleryRefreshTrigger: 0,
  currentRequestId: undefined,
  isMultiStep: undefined,
  currentStep: undefined,
  selectedTemplate: undefined,
  // TODO: temp prop until request refactor
  isRepurpose: undefined,
};

const useRequestFormStore = create<RequestFormStore>((set) => ({
  modalState: 0,
  isModalOpen: false,
  isMinimized: defaultValues.isMinimized,
  flowStep: defaultValues.flowStep,
  selectedStyle: defaultValues.selectedStyle,
  requestName: defaultValues.requestName,
  uploadingFiles: defaultValues.uploadingFiles,
  uploadingFilesWithProgress: defaultValues.uploadingFilesWithProgress,
  selectedMediaIds: defaultValues.selectedMediaIds,
  selectedMediaItems: defaultValues.selectedMediaItems,
  mediaLinks: defaultValues.mediaLinks,
  mediaGalleryRefreshTrigger: defaultValues.mediaGalleryRefreshTrigger,
  currentRequestId: defaultValues.currentRequestId,
  isMultiStep: defaultValues.isMultiStep,
  currentStep: defaultValues.currentStep,
  selectedTemplate: defaultValues.selectedTemplate,
  // TODO: temp prop until request refactor
  isRepurpose: defaultValues.isRepurpose,

  setRequestName: (requestName) => set(() => ({ requestName })),
  setUploadingFiles: (uploadingFiles) => set(() => ({ uploadingFiles })),

  // Enhanced upload management methods
  addUploadingFile: (file) =>
    set((state) => ({
      uploadingFilesWithProgress: [...state.uploadingFilesWithProgress, file],
    })),

  updateUploadProgress: (fileId, progress) =>
    set((state) => ({
      uploadingFilesWithProgress: state.uploadingFilesWithProgress.map((file) =>
        file.id === fileId ? { ...file, progress } : file,
      ),
    })),

  updateUploadStatus: (fileId, status, error) =>
    set((state) => ({
      uploadingFilesWithProgress: state.uploadingFilesWithProgress.map((file) =>
        file.id === fileId ? { ...file, status, error } : file,
      ),
    })),

  removeUploadingFile: (fileId) =>
    set((state) => {
      const newFiles = state.uploadingFilesWithProgress.filter((file) => file.id !== fileId);

      return {
        uploadingFilesWithProgress: newFiles,
      };
    }),

  setCurrentRequestId: (id) => set(() => ({ currentRequestId: id })),
  setCurrentStep: (step) => set({ currentStep: step }),
  setFlowStep: (flowStep) => set({ flowStep }),
  setSelectedStyle: (selectedStyle) => set({ selectedStyle }),
  setSelectedTemplate: (template) => set({ selectedTemplate: template }),
  setSelectedMediaIds: (selectedMediaIds) => set({ selectedMediaIds }),

  // Selected media items methods (with URLs)
  setSelectedMediaItems: (selectedMediaItems) => {
    return set({ selectedMediaItems });
  },

  addSelectedMediaItem: (mediaItem) => {
    return set((state) => {
      // Check if item already exists
      const exists = state.selectedMediaItems.some((item) => item.id === mediaItem.id);
      if (exists) {
        return state; // Don't add duplicates
      }

      // Update both selectedMediaItems and selectedMediaIds for backward compatibility
      const newSelectedMediaItems = [...state.selectedMediaItems, mediaItem];
      const newSelectedMediaIds = [...state.selectedMediaIds, mediaItem.id];

      return {
        selectedMediaItems: newSelectedMediaItems,
        selectedMediaIds: newSelectedMediaIds,
      };
    });
  },

  removeSelectedMediaItem: (mediaId) => {
    return set((state) => {
      // Remove from both arrays
      const newSelectedMediaItems = state.selectedMediaItems.filter((item) => item.id !== mediaId);
      const newSelectedMediaIds = state.selectedMediaIds.filter((id) => id !== mediaId);

      return {
        selectedMediaItems: newSelectedMediaItems,
        selectedMediaIds: newSelectedMediaIds,
      };
    });
  },

  // Media links methods
  setMediaLinks: (mediaLinks) => {
    return set({ mediaLinks });
  },

  addMediaLink: (link) => {
    return set((state) => {
      // Safely handle the case where mediaLinks might be undefined
      const currentLinks = state.mediaLinks || [];
      const newLinks = [...currentLinks, link];
      return { mediaLinks: newLinks };
    });
  },

  removeMediaLink: (index) => {
    return set((state) => {
      // Safely handle the case where mediaLinks might be undefined
      const currentLinks = state.mediaLinks || [];
      const newLinks = currentLinks.filter((_, i) => i !== index);
      return { mediaLinks: newLinks };
    });
  },

  triggerMediaGalleryRefresh: () => {
    set((state) => {
      const newState = {
        ...state,
        mediaGalleryRefreshTrigger: state.mediaGalleryRefreshTrigger + 1,
      };
      return newState;
    });
  },

  openModal: (options) =>
    set(() => ({
      isModalOpen: true,
      isMinimized: false,
      ...options,
      initialValues: options?.initialValues ?? {},
    })),
  minimizeModal: (options) => set(() => ({ isModalOpen: false, isMinimized: true, ...options })),
  discardModal: () =>
    set(() => ({
      isModalOpen: false,
      isMinimized: false,
      isMultiStep: undefined,
      currentStep: undefined,
      flowStep: 'style',
      selectedStyle: 'template',
      selectedTemplate: undefined,
      // TODO: temp prop until request refactor
      isRepurpose: undefined,
    })),
  reset: () =>
    set((state) => ({ ...defaultValues, isModalOpen: false, modalState: state.modalState + 1 })),
}));

export { useRequestFormStore };
